import React, { useContext } from "react";
import styled from "styled-components/macro";
import LabelledTextInput from "components/shared/LabelledTextInput";
import { Button } from "components/shared/standardElements";
import { ModalContext } from "components/shared/modal/MultiPageModal";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { COLORS, CONSTANTS, FONTSIZE } from "helpers/designConstants";
import { RECEIVE_CHOICE, formatPhoneNumber, isValidPhone, isValidEmail } from "helpers/bulkCheckout";

const ShippingStep = ({ dropship, receiveChoice, onReceiveChoice, choiceError, register, errors, onNext }) => {
  const modalNav = useContext(ModalContext);
  const showAddress =
    dropship === "required" ||
    (dropship === "optional" && receiveChoice === RECEIVE_CHOICE.SHIP);

  return (
    <Page>
      <Body>
      {dropship === "optional" && (
        <ChoiceBlock>
          <ChoicePrompt>How will you receive this order?</ChoicePrompt>
          <ChoiceButtons>
            <Button
              type="button"
              highlighted={receiveChoice === RECEIVE_CHOICE.PICKUP}
              onClick={() => onReceiveChoice(RECEIVE_CHOICE.PICKUP)}
            >
              Pick up my order
            </Button>
            <Button
              type="button"
              highlighted={receiveChoice === RECEIVE_CHOICE.SHIP}
              onClick={() => onReceiveChoice(RECEIVE_CHOICE.SHIP)}
            >
              Ship this order
            </Button>
          </ChoiceButtons>
          {choiceError && receiveChoice == null && (
            <ChoiceError>Please choose how you'll receive this order</ChoiceError>
          )}
          {receiveChoice === RECEIVE_CHOICE.PICKUP && (
            <Reassurance>You'll pick this order up in person.</Reassurance>
          )}
        </ChoiceBlock>
      )}

      {dropship === "required" && <Heading>Where should this ship?</Heading>}

      {showAddress && (
        <Form>
          <FieldGroup>
            <LabelledTextInput
              labelText="Name (required)"
              error={errors.name}
              {...register("name", { required: "Name Required" })}
            />
            <ErrorMessage>{errors.name?.message}</ErrorMessage>
          </FieldGroup>
          <FieldGroup>
            <LabelledTextInput
              labelText="Email (required)"
              error={errors.email}
              {...register("email", {
                required: "Email Required",
                validate: (v) => isValidEmail(v) || "Invalid email address"
              })}
            />
            <ErrorMessage>{errors.email?.message}</ErrorMessage>
          </FieldGroup>
          <FieldGroup>
            <LabelledTextInput
              labelText="Phone (required)"
              error={errors.phone}
              {...register("phone", {
                required: "Phone Required",
                validate: (v) => isValidPhone(v) || "Invalid phone number format. Use XXX-XXX-XXXX",
                onChange: (e) => {
                  e.target.value = formatPhoneNumber(e.target.value);
                }
              })}
            />
            <ErrorMessage>{errors.phone?.message}</ErrorMessage>
          </FieldGroup>
          <LabelledTextInput labelText="Company (optional)" {...register("company")} />
          <FieldGroup>
            <LabelledTextInput
              labelText="Street Address (required)"
              error={errors.streetAddress}
              {...register("streetAddress", { required: "Street Address Required" })}
            />
            <ErrorMessage>{errors.streetAddress?.message}</ErrorMessage>
          </FieldGroup>
          <LabelledTextInput labelText="Apartment, suite, unit, etc. (optional)" {...register("address2")} />
          <CityStateZip>
            <CityWrapper>
              <LabelledTextInput
                labelText="City (required)"
                error={errors.city}
                {...register("city", { required: "City is Required" })}
              />
              <ErrorMessage>{errors.city?.message}</ErrorMessage>
            </CityWrapper>
            <StateWrapper>
              <LabelledTextInput
                labelText="State (required)"
                error={errors.state}
                {...register("state", { required: "State is Required" })}
              />
              <ErrorMessage>{errors.state?.message}</ErrorMessage>
            </StateWrapper>
            <ZipWrapper>
              <LabelledTextInput
                labelText="ZIP Code (required)"
                error={errors.zip}
                {...register("zip", { required: "ZIP Code is Required" })}
              />
              <ErrorMessage>{errors.zip?.message}</ErrorMessage>
            </ZipWrapper>
          </CityStateZip>
          <LabelledTextInput labelText="Extra Details (optional)" {...register("extra")} />
        </Form>
      )}
      </Body>
      <FooterBar>
        <NextButton type="button" onClick={() => onNext(modalNav?.onNavigateForward)}>
          Next
          <ArrowForwardIosIcon style={{ width: "18px" }} />
        </NextButton>
      </FooterBar>
    </Page>
  );
};

// NOTE: port the CityInput/StateInput/ZipInput widths + @media(max-width:768px)
// breakpoints verbatim from ShippingConfirmation.js:253-279 so the 3-across row
// stacks on tablet. Body scrolls independently; FooterBar stays pinned.
const Page = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: 1fr min-content;
`;
const Body = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  text-align: left;
  overflow-y: auto;
  padding: 20px;
`;
const FooterBar = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0 30px;
  background: ${COLORS.tertiary};
  border-radius: 0 0 ${CONSTANTS.borderRadius} ${CONSTANTS.borderRadius};
`;
const NextButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 5px;
`;
const Heading = styled.div`
  text-align: center;
  color: ${COLORS.text};
`;
const ChoiceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
`;
const ChoicePrompt = styled.div`
  color: ${COLORS.text};
  font-size: ${FONTSIZE.medium};
`;
const ChoiceButtons = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
`;
const ChoiceError = styled.div`
  color: ${COLORS.alertRed};
  font-size: ${FONTSIZE.xsmall};
`;
const Reassurance = styled.div`
  color: ${COLORS.text};
  font-size: ${FONTSIZE.small};
`;
const Form = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;
const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;
const ErrorMessage = styled.div`
  height: 15px;
  color: ${COLORS.alertRed};
  font-size: ${FONTSIZE.xsmall};
  padding-top: 0px;
  padding-right: 5px;
`;
const CityStateZip = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 15px;
`;
const CityWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  @media (max-width: 768px) { width: 100%; }
`;
const StateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 20%;
  @media (max-width: 768px) { width: 35%; }
`;
const ZipWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 23%;
  @media (max-width: 768px) { width: 60%; }
`;

export default ShippingStep;
